// Automaticity (the "beyond school" SPEED lever, sibling to the depth lever).
//
// A child is "automatic" at a skill when they answer it not just accurately
// but FAST — the mark of a fact that's been committed to fluency rather than
// re-derived each time. This is a PURE read over the per-skill latency
// aggregate that aggregateSkillStatesFromAttempts already computes; it does
// NOT change the mastery band (that calibration is a separate, founder-owned
// decision). Today it feeds the enrichment evaluator's `automatic` input
// (so promote-flavored rules can fire for a fast+fluent kid) and is available
// for any future "fluent requires automaticity" gate.
import type { SkillState } from "@/types/progression";

interface AutomaticityOpts {
  /** Median-latency ceiling (ms) at or below which the skill counts as
   *  automatic. CALIBRATION TARGET — math facts are quicker than a reading
   *  item, so callers pass a domain-appropriate value. Default tuned for
   *  single-step math facts. */
  thresholdMs?: number;
  /** Minimum number of timed correct samples before we'll judge speed at all
   *  (avoids calling a kid "automatic" off one lucky-fast answer). */
  minSamples?: number;
}

export const DEFAULT_MATH_AUTOMATICITY_MS = 4000;
export const DEFAULT_READING_AUTOMATICITY_MS = 8000;
/** Default minimum number of timed correct samples before speed is
  *  judged at all. Exported so callers that gate on "enough speed
  *  evidence" (e.g. deriveMasteryBand's automaticity calibration) share
  *  ONE source of truth for the evidence floor. CALIBRATION TARGET. */
export const DEFAULT_AUTOMATICITY_MIN_SAMPLES = 5;
const DEFAULT_MIN_SAMPLES = DEFAULT_AUTOMATICITY_MIN_SAMPLES;

/** True iff the skill's recent correct answers are both numerous enough and
 *  fast enough to count as automatic. Absent/insufficient latency data → false
 *  (we never over-claim speed). Pure. */
export function isAutomatic(
  state: Pick<SkillState, "medianCorrectLatencyMs" | "automaticitySampleCount"> | null | undefined,
  opts: AutomaticityOpts = {},
): boolean {
  if (!state) return false;
  const threshold = opts.thresholdMs ?? DEFAULT_MATH_AUTOMATICITY_MS;
  const minSamples = opts.minSamples ?? DEFAULT_MIN_SAMPLES;
  const samples = state.automaticitySampleCount ?? 0;
  const median = state.medianCorrectLatencyMs;
  if (samples < minSamples) return false;
  if (median == null || !Number.isFinite(median)) return false;
  return median <= threshold;
}
